import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { useWireAdminApi } from "../../../../common/hooks/use-wire-admin-api.hook";
import { VendorPlan } from "../../our-vendors/models/vendor.model";

const labelClass = "mb-1.5 text-xs font-medium text-muted-foreground";

interface LegacyWireImportCardProps {
    vendor: any;
    setVendor: (vendor: any) => void;
    /** The plan the vendor is on (the saved one is what the server uses). */
    plan?: VendorPlan;
    /** Fields the server already saved on its own, so they are not "unsaved". */
    onServerSaved?: (patch: Record<string, any>) => void;
}

/**
 * TEMPORARY — onboards a vendor who was already paying by wire before plans
 * existed: records what they pay, how often and until when, so the normal wire
 * renewal cycle takes over. Issues no invoice and charges nothing.
 *
 * Delete this file, its use in vendor-plan-billing.form.tsx, the
 * `importWireCustomer` hook call and the LEGACY_WIRE i18n block once every
 * legacy vendor is imported.
 */
export const LegacyWireImportCard = ({ vendor, setVendor, plan, onServerSaved }: LegacyWireImportCardProps) => {
    const { t } = useTranslation();
    const wireAdminApi = useWireAdminApi();
    const [interval, setInterval] = useState<"month" | "year">("month");
    const [amount, setAmount] = useState("");
    const [paidThrough, setPaidThrough] = useState("");
    const [startedAt, setStartedAt] = useState("");
    const [saving, setSaving] = useState(false);

    const amountValue = Number(amount);
    const valid = Number.isFinite(amountValue) && amountValue > 0 && !!paidThrough;

    const submit = async () => {
        if (!vendor?._id || !valid) return;
        setSaving(true);
        try {
            const res = await wireAdminApi.importWireCustomer(vendor._id, {
                interval,
                amount: amountValue,
                paidThrough,
                ...(startedAt ? { startedAt } : {}),
            });
            const patch = {
                paymentMethod: "wire",
                wireInterval: res.wireInterval,
                wirePriceAmount: res.wirePriceAmount,
                wireCurrentPeriodEnd: res.wireCurrentPeriodEnd,
                planStartedAt: res.planStartedAt,
            };
            setVendor({ ...vendor, ...patch });
            onServerSaved?.(patch);
            toast.success(t("ADMIN_VENDOREDIT.LEGACY_WIRE.DONE"));
        } catch (err: any) {
            // The server says exactly what is wrong (free plan, past date, card subscription…).
            toast.error(err?.response?.data?.message || t("ADMIN_VENDOREDIT.LEGACY_WIRE.FAILED"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="md:col-span-2 rounded-lg border p-4" data-testid="vendor-legacy-wire-import">
            <p className="mb-1 text-sm font-medium">{t("ADMIN_VENDOREDIT.LEGACY_WIRE.TITLE")}</p>
            <p className="mb-4 text-xs text-muted-foreground">
                {t("ADMIN_VENDOREDIT.LEGACY_WIRE.BODY", { plan: plan?.name || "" })}
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                    <Label htmlFor="legacy-wire-interval" className={labelClass}>{t("ADMIN_VENDOREDIT.LEGACY_WIRE.INTERVAL")}</Label>
                    <Select value={interval} onValueChange={(v) => setInterval(v as "month" | "year")}>
                        <SelectTrigger id="legacy-wire-interval" className="w-full" data-testid="legacy-wire-interval">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="month">{t("ADMIN_VENDOREDIT.LEGACY_WIRE.MONTHLY")}</SelectItem>
                            <SelectItem value="year">{t("ADMIN_VENDOREDIT.LEGACY_WIRE.YEARLY")}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label htmlFor="legacy-wire-amount" className={labelClass}>
                        {t("ADMIN_VENDOREDIT.LEGACY_WIRE.AMOUNT", { currency: (plan?.currency || "usd").toUpperCase() })}
                    </Label>
                    <Input
                        id="legacy-wire-amount"
                        type="number"
                        min={1}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                    />
                    <p className="mt-1.5 text-xs text-muted-foreground">{t("ADMIN_VENDOREDIT.LEGACY_WIRE.AMOUNT_HINT")}</p>
                </div>
                <div>
                    <Label htmlFor="legacy-wire-paid-through" className={labelClass}>{t("ADMIN_VENDOREDIT.LEGACY_WIRE.PAID_THROUGH")}</Label>
                    <Input
                        id="legacy-wire-paid-through"
                        type="date"
                        value={paidThrough}
                        onChange={(e) => setPaidThrough(e.target.value)}
                    />
                    <p className="mt-1.5 text-xs text-muted-foreground">{t("ADMIN_VENDOREDIT.LEGACY_WIRE.PAID_THROUGH_HINT")}</p>
                </div>
                <div>
                    <Label htmlFor="legacy-wire-started-at" className={labelClass}>{t("ADMIN_VENDOREDIT.LEGACY_WIRE.STARTED_AT")}</Label>
                    <Input
                        id="legacy-wire-started-at"
                        type="date"
                        value={startedAt}
                        onChange={(e) => setStartedAt(e.target.value)}
                    />
                    <p className="mt-1.5 text-xs text-muted-foreground">{t("ADMIN_VENDOREDIT.LEGACY_WIRE.STARTED_AT_HINT")}</p>
                </div>
            </div>
            <Button className="mt-4" variant="outline" disabled={!valid || saving} onClick={submit}>
                {t("ADMIN_VENDOREDIT.LEGACY_WIRE.SUBMIT")}
            </Button>
        </div>
    );
};
